"use client";

import Link from 'next/link';
import { motion } from 'framer-motion';
import Image from 'next/image';

/**
 * Section "Suivi en ligne" pour la homepage.
 * Met en avant l'espace personnel exclusif comme differenciateur sectoriel.
 * Layout 2 colonnes : argumentaire a gauche + mockup visuel de l'espace cliente a droite.
 */
const OnlineFollowupSection = ({ hideCta = false }) => {
    return (
        <section className="bg-gradient-to-br from-[#EDF0EA] via-[#FAFAF8] to-[#F2F4F0] py-20 lg:py-28 px-6 md:px-12 relative overflow-hidden">
            {/* Decorations subtiles */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#2C6E63] opacity-5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" aria-hidden />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#A13D6C] opacity-5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" aria-hidden />

            <div className="relative max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                {/* Colonne gauche : argumentaire */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.6 }}
                >
                    <span className="inline-block px-3 py-1 mb-4 text-xs font-semibold tracking-wide uppercase text-[#A13D6C] bg-[#A13D6C]/10 rounded-full">
                        🌿 Inclus dans votre accompagnement
                    </span>
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-6">
                        Votre suivi continue bien après la consultation
                    </h2>
                    <p className="text-base sm:text-lg text-[#555] leading-relaxed mb-8">
                        À l&apos;issue de votre première séance, vous bénéficiez d&apos;un espace personnel en ligne
                        où vous retrouvez l&apos;ensemble de vos protocoles, recommandations et documents personnalisés.
                        Un accompagnement structuré, accessible à tout moment, mis à jour à chaque rendez-vous.
                    </p>

                    <ul className="space-y-3 mb-10">
                        <Benefit text="Étapes de votre protocole personnalisé" />
                        <Benefit text="Compléments alimentaires avec posologie détaillée" />
                        <Benefit text="Documents PDF téléchargeables à tout moment" />
                        <Benefit text="Accessible 24h/24 sur ordinateur, tablette et mobile" />
                    </ul>

                    {!hideCta && (
                        <Link href="/reservation" aria-label="Réserver votre première séance">
                            <button className="bg-gradient-to-r from-[#2C6E63] to-[#3D8B7A] text-white px-7 py-3.5 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300">
                                Réserver ma première séance
                            </button>
                        </Link>
                    )}
                </motion.div>

                {/* Colonne droite : mockup de l'espace cliente */}
                <motion.div
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.7, delay: 0.1 }}
                    className="relative"
                >
                    <BrowserMockup />
                </motion.div>
            </div>
        </section>
    );
};

const Benefit = ({ text }) => (
    <li className="flex items-start gap-3">
        <span className="flex-shrink-0 mt-0.5 w-6 h-6 rounded-full bg-[#2C6E63] flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
        </span>
        <span className="text-[#444] text-base sm:text-lg leading-relaxed">{text}</span>
    </li>
);

/**
 * Mockup visuel d'une fenetre navigateur affichant l'espace cliente.
 * Stylise pour ressembler a une vraie capture (barre de navigation Mac-like,
 * logo Hormelys, greeting, cards de blocs avec couleurs d'accent).
 */
const BrowserMockup = () => {
    return (
        <div className="bg-white rounded-2xl shadow-2xl border border-[#E2E7DE] overflow-hidden">
            {/* Barre de navigation mock */}
            <div className="bg-[#FAFAF8] border-b border-[#E2E7DE] px-4 py-3 flex items-center gap-2">
                <div className="flex gap-1.5">
                    <span className="w-3 h-3 rounded-full bg-red-400" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400" />
                    <span className="w-3 h-3 rounded-full bg-green-400" />
                </div>
                <div className="flex-1 mx-3 bg-white rounded-md py-1 px-3 text-xs text-gray-500 border border-[#E2E7DE] truncate">
                    🔒 hormelys.com/espace-client/marie-dupont
                </div>
            </div>

            {/* Header mock avec logo + bouton */}
            <div className="px-5 py-4 border-b border-[#E2E7DE] flex items-center justify-between gap-3">
                <div className="flex flex-col items-start">
                    <div className="relative w-28 h-10">
                        <Image
                            src="/assets/logohormelys1.webp"
                            alt=""
                            fill
                            className="object-contain object-left"
                        />
                    </div>
                    <p className="text-[10px] italic text-gray-500 -mt-1">Votre espace personnel</p>
                </div>
                <span className="text-xs bg-red-500 text-white px-3 py-1.5 rounded-lg font-medium">Se déconnecter</span>
            </div>

            {/* Contenu mock */}
            <div className="px-5 py-6 space-y-4">
                <div>
                    <p className="text-[10px] text-[#A13D6C] font-semibold tracking-wide uppercase mb-1">🌿 Votre suivi naturopathique</p>
                    <h3 className="text-xl font-bold text-[#2C6E63]" style={{ fontFamily: 'Merriweather, serif' }}>
                        Bonjour Marie
                    </h3>
                    <p className="text-[10px] text-gray-400 italic">Mis à jour le 10 mai 2026 par Nathalia</p>
                </div>

                {/* Mini-cards de blocs */}
                <div className="bg-white border border-[#E2E7DE] border-l-4 border-l-[#2C6E63] rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                        <span aria-hidden>🔍</span>
                        <p className="text-sm font-bold text-[#2C6E63]">Problématiques</p>
                    </div>
                    <p className="text-xs text-gray-500 truncate">Troubles digestifs, fatigue chronique...</p>
                </div>

                <div className="bg-white border border-[#E2E7DE] border-l-4 border-l-[#A13D6C] rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                        <span aria-hidden>🌱</span>
                        <p className="text-sm font-bold text-[#A13D6C]">Étapes protocoles</p>
                    </div>
                    <p className="text-xs text-gray-500 truncate">1. Rééquilibrage alimentaire — 2. Mastication...</p>
                </div>

                <div className="bg-white border border-[#E2E7DE] border-l-4 border-l-[#3D8B7A] rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                        <span aria-hidden>💊</span>
                        <p className="text-sm font-bold text-[#3D8B7A]">Compléments alimentaires</p>
                    </div>
                    <p className="text-xs text-gray-500 truncate">Magnésium, probiotiques...</p>
                </div>

                {/* Mini-doc */}
                <div className="bg-[#FAFAF8] border border-[#E2E7DE] rounded-lg p-3 flex items-center gap-3">
                    <span className="text-xl">📄</span>
                    <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold truncate">Protocole alimentaire — Mai 2026</p>
                        <p className="text-[10px] text-gray-500">PDF · 1.2 Mo</p>
                    </div>
                    <span className="text-[10px] text-[#A13D6C] font-semibold">Voir</span>
                </div>
            </div>
        </div>
    );
};

export default OnlineFollowupSection;
